import type { ReactElement } from 'react';
import { Route, Routes } from 'react-router-dom';
import { coreExtensionData } from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { CreateTemplatesPage } from '../create/CreateTemplatesPage';
import { TemplateFormPage } from './TemplateFormPage';

/**
 * Both provisioning routes are taken over above the scaffolder page rather than
 * inside its templates sub-page, because a sub-page renders inside the page's
 * own layout - the "Create" title bar and the Templates/Tasks/Actions tab strip
 * - and the prototype has neither: each view is a page with its own header.
 * These two carry their own, so they replace the page element for their paths.
 *
 * Everything else falls through to the stock element, so the task pages and
 * every route the scaffolder resolves through its own route refs are untouched
 * and keep the tab strip they belong to.
 */
const ScaffolderRoutes = ({ stock }: { stock: ReactElement }) => (
  <Routes>
    <Route path="templates" element={<CreateTemplatesPage />} />
    <Route
      path="templates/:namespace/:templateName"
      element={<TemplateFormPage />}
    />
    <Route path="*" element={stock} />
  </Routes>
);

export const scaffolderPageExtension = scaffolderPlugin
  .getExtension('page:scaffolder')
  .override({
    factory(originalFactory) {
      const original = originalFactory();
      return [
        ...original,
        // Emitted after the original output, which is deduplicated by data ref:
        // the element is replaced while the page's path, title and route ref
        // carry over, so /create stays where the rest of the app links to it.
        coreExtensionData.reactElement(
          <ScaffolderRoutes
            stock={original.get(coreExtensionData.reactElement)}
          />,
        ),
      ];
    },
  });
